import { Box, Divider, Paper, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../../componentes/notificationProvider";
import NewAlertModal from "../../../../componentes/newAlertModal";
import FlowHeader from "../components/flowHeader";
import FlowTable from "../components/flowTable";
import { useTabStyles } from "../components/styles";
import ModalAsn from "./modalAsn";
import MassAsns from "./massAsns";
import { asnColumns } from "./columns";

import { Asn, AsnPayload } from "./types";
import api from "../../../../services/api";
import { AxiosError } from "axios";

export default function Asns() {
  const [search, setSearch] = useState<string>("");
  const [asns, setAsns] = useState<Asn[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isMassMode, setIsMassMode] = useState<boolean>(false);
  const [tableKey, setTableKey] = useState<number>(0);

  const [openAsnModal, setOpenAsnModal] = useState<boolean>(false);
  const [editingAsn, setEditingAsn] = useState<Asn | null>(null);
  const [savingAsn, setSavingAsn] = useState<boolean>(false);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedAsn, setSelectedAsn] = useState<Asn | null>(null);

  const theme = useTheme();
  const styles = useTabStyles(theme);
  const notify = useNotification();

  useEffect(() => {
    getAsns();
  }, []);

  const getAsns = async () => {
    try {
      setLoading(true);

      const response = await api.get("/easyFlow2.0/asn");

      setAsns(Array.isArray(response.data?.data) ? response.data.data : []);
      setTableKey((key) => key + 1);
    } catch (error: any) {
      console.error(error);
      notify(
        `Error:${
          error?.message || "Error: Não foi possível se conectar com o servidor"
        } `,
        "error",
        6000,
      );
    } finally {
      setLoading(false);
    }
  };

  const rowsForDisplay = useMemo(() => {
    if (!search.trim()) return asns;

    const searchLower = search.toLowerCase();
    return asns.filter((asn) =>
      Object.values(asn).some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(searchLower),
      ),
    );
  }, [asns, search]);

  const handleOpenCreate = () => {
    setEditingAsn(null);
    setOpenAsnModal(true);
  };

  const handleEditRow = (asn: Asn) => {
    setEditingAsn(asn);
    setOpenAsnModal(true);
  };

  const handleSaveAsn = async (payload: AsnPayload, id?: number) => {
    try {
      setSavingAsn(true);

      if (id) {
        await api.put(`/easyFlow2.0/asn/${id}`, {
          ...payload,
        });
        notify("ASN atualizado com sucesso", "success");
      } else {
        await api.post(`/easyFlow2.0/asn`, {
          ...payload,
        });
        notify("ASN criado com sucesso", "success");
      }

      setOpenAsnModal(false);
      setEditingAsn(null);
      await getAsns();
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.error ||
          error?.message ||
          "Não foi possível salvar o ASN",
        "error",
      );
    } finally {
      setSavingAsn(false);
    }
  };

  const handleDeleteRow = (asn: Asn) => {
    setSelectedAsn(asn);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAsn) return;
    try {
      await api.delete(`/easyFlow2.0/asn/${selectedAsn.id}`);
      notify("ASN removido com sucesso!", "success", 3000);
      await getAsns();
    } catch (error) {
      if (error instanceof AxiosError)
        notify(
          error.response?.data?.error || "Erro ao remover ASN",
          "error",
          3000,
        );
      else notify("Não foi possível se conectar ao servidor!", "error", 3000);
    } finally {
      setOpenDeleteModal(false);
      setSelectedAsn(null);
    }
  };

  return (
    <Paper elevation={0} sx={styles.paper}>
      {isMassMode ? (
        <MassAsns
          asns={asns}
          search={search}
          onSearchChange={setSearch}
          onBack={() => setIsMassMode(false)}
          onSaved={getAsns}
        />
      ) : (
        <>
          <FlowHeader
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Pesquisar ASNs"
            onEnterEditMode={() => setIsMassMode(true)}
            disableEditMode={loading}
            onAdd={handleOpenCreate}
            onRefresh={getAsns}
            loading={loading}
          />
          <Divider
            flexItem
            sx={{ my: 1, borderColor: theme.palette.divider }}
          />
          <Box sx={styles.boxTable}>
            <FlowTable<Asn>
              key={`asns-${tableKey}`}
              id="asnsFlow"
              rows={rowsForDisplay}
              columns={asnColumns}
              loadingTable={loading}
              onEditRow={handleEditRow}
              onDeleteRow={handleDeleteRow}
            />
          </Box>
        </>
      )}

      <ModalAsn
        open={openAsnModal}
        loading={savingAsn}
        asnData={editingAsn}
        onClose={() => {
          setOpenAsnModal(false);
          setEditingAsn(null);
        }}
        onSave={handleSaveAsn}
      />

      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        textContent={`Tem certeza que deseja excluir o ASN ${
          selectedAsn?.nome_asn ?? ""
        }?`}
        handleButton={handleConfirmDelete}
        handleCancel={() => setSelectedAsn(null)}
        severity={"error"}
        singleButton={false}
      />
    </Paper>
  );
}
